import { Message } from 'elements/minute/sidebar/sidebarComponents/atomic/Message';
import { OptionRowValue } from 'elements/minute/sidebar/sidebarComponents/atomic/OptionRowValue';
import { SliderValue } from 'elements/minute/sidebar/sidebarComponents/atomic/SliderValue';
import { ColorPalette } from 'elements/minute/sidebar/sidebarComponents/complex/ColorPalette';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import { useAccess } from 'hooks/access/useAccess';
import { useMapheimDebounced } from 'hooks/general/useMapheimDebounced';
import { useContext } from 'react';
import { IWallV2WorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';
interface Props {
    className?: string;
    marker: IWallV2WorldMarker;
}

const HEIGHT_STEPS = [0.5, 1, 1.5, 2, 2.5, 3, 5, 10, 15, 25, 50, 75];
const WIDTH_STEPS = [0.1, 0.2, 0.3, 0.4, 0.5];
const FILL_OPACITY_STEPS = [0, 0.25, 0.5, 0.75, 1];

export const WallMarkerStyleSection = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const { isExperimental } = useAccess();

    const { marker } = props;

    const isLine = marker.elementData.line?.isLine ?? false;
    const hasFloor = marker.elementData.line?.hasFloor ?? false;

    function updateHeight(value: number) {
        ctxEdit.updateWorldMarkerData(marker.id, {
            wall: {
                ...marker.elementData.wall,
                height: value
            }
        });
    }

    function updateBaseHeight(value: number) {
        ctxEdit.updateWorldMarkerData(marker.id, {
            wall: {
                ...marker.elementData.wall,
                baseHeight: value
            }
        });
    }

    function updateWidth(value: number) {
        ctxEdit.updateWorldMarkerData(marker.id, {
            line: {
                ...marker.elementData.line,
                width: value
            }
        });
    }

    function updateColor(value: string) {
        ctxEdit.updateWorldMarkerData(marker.id, {
            wall: {
                ...marker.elementData.wall,
                color: value
            }
        });
    }

    function updateFloorColor(value: string) {
        ctxEdit.updateWorldMarkerData(marker.id, {
            line: {
                ...marker.elementData.line,
                fillColor: value
            }
        });
    }

    function updateOpacity(value: number) {
        ctxEdit.updateWorldMarkerData(marker.id, {
            wall: {
                ...marker.elementData.wall,
                opacity: value
            }
        });
    }

    function updateFilled(index: number) {
        const isLine = index <= 1;
        const hasFloor = index === 1;
        ctxEdit.updateWorldMarkerData(marker.id, {
            line: {
                ...marker.elementData.line,
                isLine,
                hasFloor
            }
        });
    }

    const updateHeightDebounced = useMapheimDebounced(updateHeight, 100);
    const updateBaseHeightDebounced = useMapheimDebounced(updateBaseHeight, 100);
    const updateWidthDebounced = useMapheimDebounced(updateWidth, 100);
    const updateOpacityDebounced = useMapheimDebounced(updateOpacity, 100);

    const getSelectedIndex = () => {
        if (isLine) {
            if (marker.elementData.line.hasFloor) {
                return 1;
            }
            return 0;
        }
        return 2;
    };

    return (
        <S_WallMarkerStyleSection className={props.className}>
            <SidebarSection title={Localization.STYLE} icon={{ iconName: 'brush' }}>
                <SidebarSubsection title={Localization.FILLING} icon={{ iconName: 'fill-drip' }}>
                    <SidebarContent>
                        <OptionRowValue
                            items={[
                                { iconSet: 'far', iconName: 'rectangle' },
                                { iconSet: 'fas', iconName: 'game-board' },
                                { iconSet: 'fas', iconName: 'rectangle' }
                            ]}
                            selectedIndex={getSelectedIndex()}
                            onSelected={(index) => {
                                updateFilled(index);
                            }}
                        />
                        <S_Message type="note">
                            {getSelectedIndex() === 0 && <>{Localization.FILLING1}</>}
                            {getSelectedIndex() === 1 && <>{Localization.FILLING2}</>}
                            {getSelectedIndex() === 2 && <>{Localization.FILLING3}</>}
                        </S_Message>
                    </SidebarContent>
                </SidebarSubsection>
                <SidebarSubsection title={Localization.COLOR} icon={{ iconName: 'circle' }}>
                    <SidebarContent>
                        <ColorPalette
                            value={marker.elementData.wall.color}
                            pickerAllowed
                            onChange={(color) => {
                                updateColor(color);
                            }}
                        />
                    </SidebarContent>
                </SidebarSubsection>
                {isLine && hasFloor && (
                    <SidebarSubsection
                        title={Localization.GROUND_COLOR}
                        icon={{ iconName: 'circle' }}
                    >
                        <SidebarContent>
                            <ColorPalette
                                value={marker.elementData.line.fillColor}
                                pickerAllowed
                                onChange={(color) => {
                                    updateFloorColor(color);
                                }}
                            />
                        </SidebarContent>
                    </SidebarSubsection>
                )}
                <SidebarSubsection title={Localization.HEIGHT} icon={{ iconName: 'building' }}>
                    <SidebarContent>
                        <SliderValue
                            steps={HEIGHT_STEPS}
                            value={marker.elementData.wall.height}
                            onValueChange={updateHeightDebounced}
                        />
                    </SidebarContent>
                </SidebarSubsection>
                {isExperimental && (
                    <SidebarSubsection
                        title={Localization.BASE_HEIGHT}
                        icon={{ iconName: 'arrows-up-to-line' }}
                    >
                        <SidebarContent>
                            <SliderValue
                                steps={HEIGHT_STEPS}
                                value={marker.elementData.wall.baseHeight}
                                onValueChange={updateBaseHeightDebounced}
                            />
                        </SidebarContent>
                    </SidebarSubsection>
                )}
                {isLine && (
                    <SidebarSubsection
                        title={Localization.WIDTH}
                        icon={{ iconName: 'block-brick' }}
                    >
                        <SidebarContent>
                            <SliderValue
                                steps={WIDTH_STEPS}
                                value={marker.elementData.line.width}
                                onValueChange={updateWidthDebounced}
                            />
                        </SidebarContent>
                    </SidebarSubsection>
                )}
                <SidebarSubsection
                    title={Localization.OPACITY}
                    icon={{ iconName: 'diagram-venn' }}
                    disableBottomBorder
                >
                    <SidebarContent>
                        <SliderValue
                            steps={FILL_OPACITY_STEPS}
                            value={marker.elementData.wall?.opacity}
                            onValueChange={updateOpacityDebounced}
                        />
                    </SidebarContent>
                </SidebarSubsection>
            </SidebarSection>
        </S_WallMarkerStyleSection>
    );
};

const S_WallMarkerStyleSection = styled.div``;

const S_Message = styled(Message)`
    margin: 15px 0 0 0;
`;
